import React from "react";
import { HeroSection } from "@/components/landing/HeroSection";
import { ProblemSolutionSection } from "@/components/landing/ProblemSolutionSection";
import { ModesSection } from "@/components/landing/ModesSection";
import { FeaturesSection } from "@/components/landing/FeaturesSection";
import { HowItWorksSection } from "@/components/landing/HowItWorksSection";
import { ChatDemoSection } from "@/components/landing/ChatDemoSection";
import { TestimonialsSection } from "@/components/landing/TestimonialsSection";
import { PricingTeaser } from "@/components/landing/PricingTeaser";
import { FAQSection } from "@/components/landing/FAQSection";
import { CTASection } from "@/components/landing/CTASection";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";
import { useHreflang } from "@/i18n/useHreflang";
import { useLocalizedMeta } from "@/i18n/useLocalizedMeta";
import { useStructuredData } from "@/hooks/useStructuredData";
import {
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildHowToSchema,
  buildFeatureItemListSchema,
} from "@/lib/structuredData";
import { useTranslation } from "react-i18next";
import { detectLocale } from "@/lib/seo";
import { useLocation } from "react-router-dom";

/* Animated gradient divider */
const SectionDivider: React.FC = () => (
  <div className="relative h-px max-w-5xl mx-auto">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-border/60 to-transparent" />
  </div>
);

const Landing: React.FC = () => {
  useHreflang();
  useLocalizedMeta();

  const { t } = useTranslation();
  const location = useLocation();
  const locale = detectLocale(location.pathname);

  const faqs = t('faq.items', { returnObjects: true }) as { q: string; a: string }[];
  const faqItems = Array.isArray(faqs)
    ? faqs.map((f) => ({ question: f.q, answer: f.a }))
    : [];

  // ── Structured data — injected dynamically via JS
  // Base: Organization + WebSite (also inline in index.html for crawlers)
  useStructuredData(buildOrganizationSchema(), 'landing-org');
  useStructuredData(buildWebSiteSchema(), 'landing-website');
  // App with AggregateRating + enriched featureList
  useStructuredData(buildSoftwareApplicationSchema(locale), 'landing-app');
  // HowTo — eligible for rich snippets in AI overviews
  useStructuredData(buildHowToSchema(locale), 'landing-howto');
  // FAQ — drives featured snippets and AI overview answers
  useStructuredData(buildFAQPageSchema(faqItems), 'landing-faq');
  // ItemList — signals topical coverage for feature keywords
  useStructuredData(buildFeatureItemListSchema(locale), 'landing-features');

  return (
    <main className="min-h-screen overflow-x-hidden bg-background">
      {/*
        LLM extraction note: The entity definition below is rendered as plain text
        for AI crawlers, search engine parsers, and language model context windows.
        It is intentionally duplicated from the hero section for maximum extraction.
      */}
      <HeroSection />
      <SectionDivider />
      <ProblemSolutionSection />
      <ModesSection />
      <FeaturesSection />
      <SectionDivider />
      <HowItWorksSection />
      <ChatDemoSection />
      <TestimonialsSection />
      <PricingTeaser />
      <FAQSection />
      <CTASection />
      <LandingFooter />
      <ScrollToTopButton />
    </main>
  );
};

export default Landing;
